/**
 * Admin Search Console panel — calls GET /admin/gsc and renders the
 * sitemap status + last-7-day top queries + top pages. Live-fetches
 * GSC on each render (no DDB cache); GSC has a ~2-day data lag so
 * frequent refreshes don't change anything anyway.
 *
 * Inherits the dashboard's auto-refresh tick via `refreshKey`.
 */
import { useEffect, useState } from 'react';
import { getGscSummary } from '../api/client';
import type { AdminGscSummary, AdminGscRow, AdminGscSection, AdminGscDailyPoint, AdminGscUrlInspection } from '../types';

function fmtPct(x: number): string { return (x * 100).toFixed(2) + '%'; }
function fmtPos(x: number): string { return x.toFixed(1); }
function fmtDate(s: string | null): string {
  if (!s) return '—';
  return new Date(s).toLocaleString();
}

export function GscSummarySection({ refreshKey = 0 }: { refreshKey?: number }) {
  const [data, setData] = useState<AdminGscSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getGscSummary()
      .then(d => { if (!cancelled) { setData(d); setError(null); } })
      .catch(err => { if (!cancelled) setError(err instanceof Error ? err.message : 'failed'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [refreshKey]);

  if (error && !data) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        Search Console: {error}
      </div>
    );
  }
  if (!data) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-6 text-sm text-gray-400">
        {loading ? 'Loading Search Console…' : 'No data'}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      {/* Header — totals + window + week-over-week deltas */}
      <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold">Google Search Console</h2>
          <span className="text-xs text-gray-500 font-mono">{data.site}</span>
        </div>
        <div className="text-xs text-gray-500">
          {data.window.startDate} → {data.window.endDate} ·
          <span className="ml-1 font-semibold text-gray-900">{data.totals.clicks.toLocaleString()}</span> clicks
          <Wow current={data.totals.clicks} prev={data.prevPeriod?.clicks} />
          ·
          <span className="ml-1 font-semibold text-gray-900">{data.totals.impressions.toLocaleString()}</span> impressions
          <Wow current={data.totals.impressions} prev={data.prevPeriod?.impressions} />
          {!data.prevPeriod && data.daily && data.daily.length > 0 && (
            <span className="ml-2 text-[10px] text-gray-400">
              (w/w in {Math.max(0, 14 - data.daily.length)}d)
            </span>
          )}
        </div>
      </div>

      {/* Indexing health — routine URL Inspection on a small fixed list */}
      {data.indexingHealth && data.indexingHealth.length > 0 && (
        <div className="border-b border-gray-100 px-6 py-4">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">Indexing health (sampled URLs)</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                <th className="py-2 font-medium">URL</th>
                <th className="py-2 font-medium">Coverage</th>
                <th className="py-2 font-medium text-center">Verdict</th>
                <th className="py-2 font-medium text-right">Last crawl</th>
              </tr>
            </thead>
            <tbody>
              {data.indexingHealth.map(u => <IndexingRow key={u.url} u={u} />)}
            </tbody>
          </table>
          <p className="mt-2 text-[11px] text-gray-400">
            Inspected on every refresh. PASS = indexed; NEUTRAL = discovered/crawled but not indexed yet (typical for fresh content).
          </p>
        </div>
      )}

      {/* 30-day sparkline — only renders once the snapshotter has captured data */}
      {data.daily && data.daily.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-gray-100 border-b border-gray-100">
          <SparklineCell label="Clicks (30d)" points={data.daily} field="clicks" stroke="#e151af" />
          <SparklineCell label="Impressions (30d)" points={data.daily} field="impressions" stroke="#10b981" />
        </div>
      )}

      {/* Sitemaps */}
      <div className="border-b border-gray-100 px-6 py-4">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">Sitemaps</h3>
        {data.sitemaps.length === 0 ? (
          <p className="text-sm text-gray-400">No sitemaps registered.</p>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                <th className="py-2 font-medium">Path</th>
                <th className="py-2 font-medium">Last submitted</th>
                <th className="py-2 font-medium">Last downloaded</th>
                <th className="py-2 font-medium text-right">Errors</th>
                <th className="py-2 font-medium text-right">Warnings</th>
                <th className="py-2 font-medium text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.sitemaps.map(s => (
                <tr key={s.path} className="border-b border-gray-50 last:border-0">
                  <td className="py-2 truncate max-w-xs">
                    <a href={s.path} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                      {s.path.replace(/^https?:\/\//, '')}
                    </a>
                  </td>
                  <td className="py-2 text-xs text-gray-500">{fmtDate(s.lastSubmitted)}</td>
                  <td className="py-2 text-xs text-gray-500">{fmtDate(s.lastDownloaded)}</td>
                  <td className={`py-2 text-right text-xs ${s.errors > 0 ? 'text-red-700 font-semibold' : 'text-gray-500'}`}>{s.errors}</td>
                  <td className={`py-2 text-right text-xs ${s.warnings > 0 ? 'text-amber-700 font-semibold' : 'text-gray-500'}`}>{s.warnings}</td>
                  <td className="py-2 text-right text-xs">
                    {s.pending
                      ? <span className="rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-amber-800">Processing</span>
                      : <span className="rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-emerald-700">OK</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Per-URL inspection — only rendered if a sitemap had warnings/errors */}
      {data.urlInspections && (
        <div className="border-b border-gray-100 px-6 py-4">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">
            URL inspection — why warnings/errors
          </h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                <th className="py-2 font-medium">URL</th>
                <th className="py-2 font-medium">Coverage</th>
                <th className="py-2 font-medium text-center">Verdict</th>
                <th className="py-2 font-medium text-right">Last crawl</th>
              </tr>
            </thead>
            <tbody>
              {data.urlInspections.urls.map(u => {
                const isPass = u.verdict === 'PASS';
                const isFail = u.verdict === 'FAIL';
                const blocked = u.robotsState === 'DISALLOWED';
                return (
                  <tr key={u.url} className="border-b border-gray-50 last:border-0">
                    <td className="py-1.5 truncate max-w-xs">
                      <a href={u.url} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
                        {u.url.replace(/^https?:\/\//, '').replace(/^xbot\.ninja/, '')}
                      </a>
                      {blocked && (
                        <span className="ml-2 inline-block rounded bg-amber-50 border border-amber-200 px-1.5 py-0.5 text-[10px] text-amber-800 align-middle">
                          robots: blocked
                        </span>
                      )}
                    </td>
                    <td className="py-1.5 text-xs text-gray-700">{u.coverage}</td>
                    <td className={`py-1.5 text-center text-xs ${isPass ? 'text-emerald-700' : isFail ? 'text-red-700' : 'text-gray-500'}`}>
                      {u.verdict}
                    </td>
                    <td className="py-1.5 text-right text-xs text-gray-500">
                      {u.lastCrawl ? new Date(u.lastCrawl).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="mt-2 text-[11px] text-gray-400">
            Inspection runs only when a sitemap has warnings or errors (saves URL Inspection quota). Coverage = Google's view of each URL.
          </p>
        </div>
      )}

      {/* Per-section breakdown — Site / Blog / Docs side by side */}
      {data.sections && data.sections.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-100 border-b border-gray-100">
          {data.sections.map(s => <SectionCard key={s.name} section={s} />)}
        </div>
      )}

      {/* Top queries + top pages, side by side on md+ */}
      <div className="grid md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-gray-100">
        <RowsTable title="Top queries (7d)" rows={data.topQueries} keyHeader="Query" />
        <RowsTable title="Top pages (7d)" rows={data.topPages} keyHeader="Page" linkify />
      </div>
    </div>
  );
}

function Wow({ current, prev }: { current: number; prev: number | undefined }) {
  if (prev === undefined) return null;
  if (prev === 0) {
    return current === 0
      ? <span className="ml-1 text-gray-400">(=)</span>
      : <span className="ml-1 text-emerald-600 font-semibold">(new)</span>;
  }
  const delta = ((current - prev) / prev) * 100;
  const sign = delta >= 0 ? '+' : '';
  const cls = delta > 0 ? 'text-emerald-600' : delta < 0 ? 'text-red-600' : 'text-gray-400';
  return <span className={`ml-1 ${cls} font-semibold`}>({sign}{delta.toFixed(0)}%)</span>;
}

function IndexingRow({ u }: { u: AdminGscUrlInspection }) {
  const isPass = u.verdict === 'PASS';
  const isFail = u.verdict === 'FAIL';
  // "URL is unknown to Google" / "Discovered - currently not indexed" come
  // back as NEUTRAL — surface them in amber, not red, since both are normal
  // for fresh content waiting on the crawl budget queue.
  const verdictCls = isPass ? 'text-emerald-700' : isFail ? 'text-red-700' : 'text-amber-700';
  return (
    <tr className="border-b border-gray-50 last:border-0">
      <td className="py-1.5 truncate max-w-xs">
        <a href={u.url} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">
          {u.url.replace(/^https?:\/\/xbot\.ninja/, '') || '/'}
        </a>
      </td>
      <td className="py-1.5 text-xs text-gray-700">{u.coverage}</td>
      <td className={`py-1.5 text-center text-xs font-semibold ${verdictCls}`}>{u.verdict}</td>
      <td className="py-1.5 text-right text-xs text-gray-500">
        {u.lastCrawl ? new Date(u.lastCrawl).toLocaleDateString() : 'never'}
      </td>
    </tr>
  );
}

function SparklineCell({ label, points, field, stroke }: { label: string; points: AdminGscDailyPoint[]; field: 'clicks' | 'impressions'; stroke: string }) {
  // Inline SVG sparkline — no chart lib. 30 daily values mapped to a
  // 0-100 viewBox; flat-zero series renders as a baseline. Today's tip
  // gets a small dot so the eye lands on the latest data point.
  const W = 280, H = 48, PAD = 2;
  const ys = points.map(p => p[field]);
  const max = Math.max(...ys, 1);
  const stepX = (W - PAD * 2) / Math.max(points.length - 1, 1);
  const path = points.map((p, i) => {
    const x = PAD + i * stepX;
    const y = H - PAD - (p[field] / max) * (H - PAD * 2);
    return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
  }).join(' ');
  const last = points[points.length - 1];
  const lastX = PAD + (points.length - 1) * stepX;
  const lastY = H - PAD - ((last[field] || 0) / max) * (H - PAD * 2);
  const total = ys.reduce((a, b) => a + b, 0);
  return (
    <div className="px-6 py-4">
      <div className="flex items-baseline justify-between mb-1">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">{label}</p>
        <p className="text-sm font-mono text-gray-900">{total.toLocaleString()}</p>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-12 block" preserveAspectRatio="none">
        <path d={path} fill="none" stroke={stroke} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
        <circle cx={lastX} cy={lastY} r="2.5" fill={stroke} />
      </svg>
      <p className="text-[10px] text-gray-400 mt-1 font-mono">{points[0].date} → {last.date}</p>
    </div>
  );
}

function SectionCard({ section }: { section: AdminGscSection }) {
  return (
    <div className="px-6 py-4">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400">{section.name}</h3>
        <div className="text-xs text-gray-500">
          <span className="font-semibold text-gray-900">{section.totals.clicks.toLocaleString()}</span> clicks ·
          <span className="ml-1 font-semibold text-gray-900">{section.totals.impressions.toLocaleString()}</span> impr.
        </div>
      </div>
      {section.topQueries.length === 0 && section.topPages.length === 0 ? (
        <p className="text-sm text-gray-400">No data in this window.</p>
      ) : (
        <div className="space-y-3">
          <SectionMiniList label="Top queries" rows={section.topQueries.slice(0, 5)} />
          <SectionMiniList label="Top pages" rows={section.topPages.slice(0, 5)} linkify />
        </div>
      )}
    </div>
  );
}

function SectionMiniList({ label, rows, linkify = false }: { label: string; rows: AdminGscRow[]; linkify?: boolean }) {
  if (rows.length === 0) return null;
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-1">{label}</p>
      <ul className="space-y-0.5 text-xs">
        {rows.map(r => (
          <li key={r.key} className="flex items-baseline justify-between gap-2">
            <span className="truncate flex-1 min-w-0">
              {linkify
                ? <a href={r.key} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">{r.key.replace(/^https?:\/\/xbot\.ninja/, '').replace(/^https?:\/\//, '') || '/'}</a>
                : <span className="text-gray-700">{r.key}</span>}
            </span>
            <span className="font-mono text-gray-500 shrink-0">{r.clicks}/{r.impressions}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function RowsTable({ title, rows, keyHeader, linkify = false }: { title: string; rows: AdminGscRow[]; keyHeader: string; linkify?: boolean }) {
  // Backend returns up to top-100 in this window; default to showing 25
  // (the historical view) and let the operator opt in to the full list.
  const [expanded, setExpanded] = useState(false);
  const COLLAPSED = 25;
  const visible = expanded ? rows : rows.slice(0, COLLAPSED);
  const hasMore = rows.length > COLLAPSED;
  return (
    <div className="px-6 py-4">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-xs font-semibold uppercase tracking-wider text-gray-400">{title}</h3>
        {hasMore && (
          <button
            type="button"
            onClick={() => setExpanded(v => !v)}
            className="text-[11px] text-primary-600 hover:underline"
          >
            {expanded ? `Show top ${COLLAPSED}` : `Show all ${rows.length}`}
          </button>
        )}
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-gray-400">No data in this window.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
              <th className="py-2 font-medium">{keyHeader}</th>
              <th className="py-2 font-medium text-right">Clicks</th>
              <th className="py-2 font-medium text-right">Impr.</th>
              <th className="py-2 font-medium text-right">CTR</th>
              <th className="py-2 font-medium text-right">Pos.</th>
            </tr>
          </thead>
          <tbody>
            {visible.map(r => (
              <tr key={r.key} className="border-b border-gray-50 last:border-0">
                <td className="py-1.5 truncate max-w-[200px]">
                  {linkify
                    ? <a href={r.key} target="_blank" rel="noopener noreferrer" className="text-primary-600 hover:underline">{r.key.replace(/^https?:\/\//, '')}</a>
                    : <span className="text-gray-900">{r.key}</span>}
                </td>
                <td className="py-1.5 text-right font-mono text-xs">{r.clicks}</td>
                <td className="py-1.5 text-right font-mono text-xs">{r.impressions}</td>
                <td className="py-1.5 text-right font-mono text-xs text-gray-500">{fmtPct(r.ctr)}</td>
                <td className="py-1.5 text-right font-mono text-xs text-gray-500">{fmtPos(r.position)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
